import { Model } from '@vuex-orm/core'
import { Request } from '@vuex-orm/plugin-axios'

import Room from '@/models/Room'
import Configuration from '@/models/Configuration'
import RatePlan from '@/models/RatePlan'
import AppState from '@/utils/app.state'
import { DailyRatePlanPrice, MealPlanPriceBreakdown, RatePlanPriceBreakdown, TaxBreakdown } from './common/breakdown'

enum SpoApis {
  DailyOffers = 'dailyID',
  Discounts = 'discountID',
  RoomUpgrades = 'roomUpgradeID',
  BoardUpgrades = 'boardUpgradeID',
  BoardDiscounts = 'boardDiscountID'
}

/* RPP fields and corresponding apis */
const SPO_APIS: [SpoApis, string][] = [
  [SpoApis.DailyOffers, '/content/v2/spos/getspodayoffersbypropertyids'],
  [SpoApis.Discounts, '/content/v2/spos/getspodiscountsbypropertyids'],
  [SpoApis.RoomUpgrades, '/content/v2/spos/getspofreeroomupgradesbypropertyids'],
  [SpoApis.BoardUpgrades, '/content/v2/spos/getspofreeboardupgradesbypropertyids'],
  [SpoApis.BoardDiscounts, '/content/v2/spos/getspoboarddiscountsbypropertyids'],
]

/**
 * Caching the spo response
 */
const cachedResponse = {
  data: {} as Record<SpoApis, any[]>,
  promises: {} as Record<SpoApis, Promise<any> | undefined>,
  promiseTimers: {} as Record<SpoApis, ReturnType<typeof setTimeout>>
}

/**
 * SPO item class
 */
class SpoItem {
  data: any
  id: string
  langedName: string
  type: SpoApis

  constructor(data: any, type: SpoApis) {
    this.data = data
    this.id = data.ID || data.id
    this.type = type

    this.langedName = this.data.description &&
      this.data.description[0] &&
      this.data.description[0].value
  }
}

/**
 * Requests spo data
 * @param {Request} http
 * @param {string} url
 * @returns {Promise<Response>}
 */
function requestSpo(http: Request, url: string) {
  return http.post(
    url,
    {
      propertyIDs: [AppState.currentProperty],
      languageCodes: [AppState.localeShort]
    },
    { save: false }
  )
}

export default class RatePlanPrice extends Model {
  declare id: string
  declare type: string
  declare roomID: string
  declare ratePlanID: string
  declare applicableCancellationPolicyID: number
  declare applicablePaymentPolicyID: number
  declare pricingType: string
  declare quantity: number
  declare dailyPrices: DailyRatePlanPrice[]
  declare ageGroupIds: string[] // todo?
  declare boardPrices: MealPlanPriceBreakdown[]
  declare totalPrices: RatePlanPriceBreakdown[]
  declare taxBreakdown: TaxBreakdown
  declare isAvailableOnRequest: boolean
  declare excludedTaxBreakdown: TaxBreakdown
  declare configurationId: string
  declare configuration: Configuration
  declare room: Room
  declare ratePlan: RatePlan
  declare includedExtrasIDs: number[]
  declare spo: any // todo

  static entity = 'ratePlanPrice';

  static fields() {
    return {
      id: this.uid(),
      type: this.attr('planPrice'),
      roomID: this.attr(null),
      ratePlanID: this.attr(null),
      applicableCancellationPolicyID: this.number(0),
      applicablePaymentPolicyID: this.number(0),
      pricingType: this.attr(null),
      quantity: this.number(0),
      dailyPrices: this.attr(null),
      ageGroupIds: this.attr([]),
      boardPrices: this.attr(null),
      totalPrices: this.attr(null),
      taxBreakdown: this.attr(null),
      isAvailableOnRequest: this.boolean(false),
      excludedTaxBreakdown: this.attr(null),
      configurationId: this.attr(null),
      configuration: this.belongsTo(Configuration, 'configurationId'),
      room: this.belongsTo(Room, 'roomID'),
      ratePlan: this.belongsTo(RatePlan, 'ratePlanID'),
      includedExtrasIDs: this.attr([]),
      spo: this.attr(null).nullable()
    }
  }

  get totalPrice(): number {
    return this.totalPrices && this.totalPrices[0].totalPrice
  }

  get netPrice(): number {
    return this.totalPrices && this.totalPrices[0].netPrice
  }

  get calculatedDailyPricesWithoutFees(): { date: string, price: number }[] {
    if (!Array.isArray(this.dailyPrices)) { return [] }

    return this.dailyPrices.map(dailyPrice => ({
      date: dailyPrice.date,
      price: dailyPrice.ageGroupPrices.reduce(
        (sum, ageGroupPrice) => sum + ageGroupPrice.netPrice + ageGroupPrice.taxAmount,
        0
      )
    }))
  }

  get isPackage() {
    return this.type === 'packagePlanPrice'
  }

  get isSpecialOffer() {
    return this.type === 'specialPlanPrice'
  }

  get priceWithoutFees() {
    return this.totalPrices.reduce(
      (sum, ageGroupPrice) => sum +
        ageGroupPrice.netPrice +
        ageGroupPrice.taxAmount +
        ageGroupPrice.specialOfferAmount,
      0
    )
  }

  get priceWithoutFeesAndSpecialOffer() {
    return this.totalPrices.reduce(
      (sum, ageGroupPrice) => sum + ageGroupPrice.netPrice + ageGroupPrice.taxAmount,
      0
    )
  }

  /**
   * Gets the special offer data of rate plan price
   * @param {{spo: {
   *  dailyID,
   *  discountID,
   *  roomUpgradeID,
   *  boardUpgradeID,
   *  boardDiscountID
   * }}} ratePlanPrice 
   * @returns {Promise<SpoItem|null>}
   */
  static async getSpecialOfferData(ratePlanPrice: RatePlanPrice) {
    /* No spo data */
    if (!ratePlanPrice.spo) { return null }

    /* Get spo field name */
    const spoEntry = ratePlanPrice.spo &&
      SPO_APIS.find(([field, _apiUrl]) => ratePlanPrice.spo[field] != null)!!

    /* No field found */
    if (!spoEntry) { return null }

    /* Get spo field value */
    const [fieldName, fieldApiUrl]: [SpoApis, string] = spoEntry
    const fieldValue = ratePlanPrice.spo[fieldName]

    /* Check for cache hit */
    const cache = cachedResponse.data[fieldName]
    const hit = cache && cache.find(item => item.id === fieldValue)
    if (hit) { return hit }

    try {
      /* Make a request and save it for other calls to wait for completion */
      if (!cachedResponse.promises[fieldName]) {
        cachedResponse.promises[fieldName] = requestSpo(this.api(), fieldApiUrl)
      }

      /* Get from cached promises */
      const resPromise = cachedResponse.promises[fieldName]

      /* Get the response data */
      const spoResponse = (await resPromise).response.data as any[]

      /* Convert to spo items */
      const spoItems = spoResponse.map(itemData => new SpoItem(itemData, fieldName))

      /* Cache */
      cachedResponse.data[fieldName] = spoItems

      /* Remove promises on 5s timeout when unused */
      if (cachedResponse.promiseTimers[fieldName]) {
        clearTimeout(cachedResponse.promiseTimers[fieldName])
        cachedResponse.promiseTimers[fieldName] = setTimeout(() => {
          cachedResponse.promises[fieldName] = undefined
        }, 5000)
      }

      /* Find and return a match */
      return spoItems.find(item => item.id === fieldValue) || null
    } catch (e) {
      console.error(e)

      return null
    }
  }
}
